// Generic forms for Investors, Partners, News
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageWithWrap from '../../components/editor/ImageWithWrap';
import { addImageFromUrl, createImagePasteHandler } from '../../components/editor/editorImageAlt';
import { EditorImageAltControls } from '../../components/editor/EditorImageAltControls';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { ArrowLeft, Loader2, Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify, WrapText, Square, Table as TableIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

// RTF editor styles for News snippet
const snippetEditorStyles = `
  .news-snippet-editor .ProseMirror {
    min-height: 120px;
    padding: 0.75rem 1rem;
    outline: none;
    border: 1px solid #e5e7eb;
    border-radius: 0 0 0.375rem 0.375rem;
  }
  .news-snippet-editor .ProseMirror > * + * { margin-top: 0.5em; }
  .news-snippet-editor .ProseMirror p { margin: 0; line-height: 1.5; }
  .news-snippet-editor .ProseMirror strong { font-weight: 700; }
  .news-snippet-editor .ProseMirror em { font-style: italic; }
  .news-snippet-editor .ProseMirror u { text-decoration: underline; }
  .news-snippet-editor .ProseMirror ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
  .news-snippet-editor .ProseMirror ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
  .news-snippet-editor .ProseMirror a { color: #C8473A; text-decoration: underline; }
  .news-snippet-editor .ProseMirror img { max-width: 100%; height: auto; border-radius: 6px; margin: 0.5em 0; }
  .news-snippet-editor .ProseMirror p[style*="text-align: center"] img { display: block; margin-left: auto; margin-right: auto; }
  .news-snippet-editor .ProseMirror p[style*="text-align: right"] img { display: block; margin-left: auto; }
  .news-snippet-editor .ProseMirror p[style*="text-align: justify"] { text-align: justify; }
  .news-snippet-editor .ProseMirror img[data-float="left"] { float: left; margin: 0 1em 0.5em 0; max-width: 50%; }
  .news-snippet-editor .ProseMirror img[data-float="right"] { float: right; margin: 0 0 0.5em 1em; max-width: 50%; }
  .news-snippet-editor .ProseMirror img[data-float="block"],
  .news-snippet-editor .ProseMirror img:not([data-float]) { float: none; display: block; }
  .news-snippet-editor .ProseMirror table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
  .news-snippet-editor .ProseMirror th, .news-snippet-editor .ProseMirror td { border: 1px solid #e5e7eb; padding: 0.35em 0.5em; text-align: left; }
  .news-snippet-editor .ProseMirror th { background: #f9fafb; font-weight: 600; }
  .news-snippet-editor .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #9ca3af; float: left; height: 0; pointer-events: none; }
`;

const SnippetToolbarButton = ({ onClick, isActive, children, title }) => (
  <Button type="button" variant={isActive ? 'secondary' : 'ghost'} size="sm" onClick={onClick} title={title} className="h-8 w-8 p-0">
    {children}
  </Button>
);

const NewsSnippetMenuBar = ({ editor, onUploadImageFromUrl }) => {
  if (!editor) return null;
  const addLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL:', prev);
    if (url === null) return;
    if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  const addImage = () => addImageFromUrl(editor, onUploadImageFromUrl);
  return (
    <div className="border border-b-0 border-gray-200 rounded-t-md bg-gray-50/80 p-1.5 flex flex-wrap gap-0.5">
      <SnippetToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold"><Bold className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic"><Italic className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline"><UnderlineIcon className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough"><Strikethrough className="w-4 h-4" /></SnippetToolbarButton>
      <span className="w-px h-5 bg-gray-200 mx-0.5" />
      <SnippetToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align left"><AlignLeft className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align center"><AlignCenter className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align right"><AlignRight className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justify (text wrap)"><AlignJustify className="w-4 h-4" /></SnippetToolbarButton>
      <span className="w-px h-5 bg-gray-200 mx-0.5" />
      <SnippetToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet list"><List className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><TableIcon className="w-4 h-4" /></SnippetToolbarButton>
      <span className="w-px h-5 bg-gray-200 mx-0.5" />
      <SnippetToolbarButton onClick={addLink} isActive={editor.isActive('link')} title="Link"><LinkIcon className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={addImage} title="Image (stored locally)"><ImageIcon className="w-4 h-4" /></SnippetToolbarButton>
      <span className="w-px h-5 bg-gray-200 mx-0.5" />
      <SnippetToolbarButton onClick={() => editor.chain().focus().updateAttributes('image', { float: 'left' }).run()} isActive={editor.isActive('image') && editor.getAttributes('image').float === 'left'} title="Wrap text (image left)"><WrapText className="w-4 h-4" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().updateAttributes('image', { float: 'right' }).run()} isActive={editor.isActive('image') && editor.getAttributes('image').float === 'right'} title="Wrap text (image right)"><WrapText className="w-4 h-4 rotate-180" /></SnippetToolbarButton>
      <SnippetToolbarButton onClick={() => editor.chain().focus().updateAttributes('image', { float: null }).run()} isActive={editor.isActive('image') && !editor.getAttributes('image').float} title="No wrap (block)"><Square className="w-4 h-4" /></SnippetToolbarButton>
      <EditorImageAltControls editor={editor} />
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Investor Form
export const InvestorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [funds, setFunds] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({ name: '', fund_id: '', logo_alt: '', description: '', website_url: '', investor_type: 'institutional', display_order: 0 });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const fundsRes = await axios.get(`${API}/admin/funds`, { withCredentials: true });
        setFunds(fundsRes.data);
        if (isEditing) {
          const res = await axios.get(`${API}/admin/investors/${id}`, { withCredentials: true });
          setFormData(res.data);
          if (res.data.logo_url) setLogoPreview(res.data.logo_url.startsWith('/') ? `${BACKEND_URL}${res.data.logo_url}` : res.data.logo_url);
        }
      } catch (error) { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id, isEditing]);

  const handleLogoChange = (e) => { const file = e.target.files[0]; if (file) { setLogoFile(file); setLogoPreview(URL.createObjectURL(file)); } };
  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.fund_id) { toast.error('Fill required fields'); return; }
    setSaving(true);
    try {
      const res = isEditing
        ? await axios.put(`${API}/admin/investors/${id}`, formData, { withCredentials: true })
        : await axios.post(`${API}/admin/investors`, formData, { withCredentials: true });
      if (logoFile) {
        const fd = new FormData(); fd.append('file', logoFile); fd.append('alt_text', formData.logo_alt || formData.name);
        await axios.post(`${API}/admin/investors/${res.data.investor_id}/logo`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(isEditing ? 'Updated' : 'Created');
      navigate('/admin/investors');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" /></div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6"><Button variant="ghost" onClick={() => navigate('/admin/investors')}><ArrowLeft className="w-4 h-4" /></Button><h1 className="text-2xl font-bold">{isEditing ? 'Edit Investor' : 'Add Investor'}</h1></div>
      <form onSubmit={handleSubmit} className="max-w-2xl bg-white rounded-lg border p-6 space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required /></div>
          <div><Label>Fund *</Label><Select value={formData.fund_id} onValueChange={(v) => handleChange('fund_id', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{funds.map(f => <SelectItem key={f.fund_id} value={f.fund_id}>{f.name}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <div><Label>Logo</Label><div className="flex items-start gap-4">{logoPreview && <img src={logoPreview} alt="Preview" className="w-20 h-20 rounded-lg object-contain bg-gray-50 border" />}<div className="flex-1"><Input type="file" accept="image/*" onChange={handleLogoChange} className="mb-2" /><Input placeholder="Alt text" value={formData.logo_alt} onChange={(e) => handleChange('logo_alt', e.target.value)} /></div></div></div>
        <div className="grid md:grid-cols-2 gap-6">
          <div><Label>Type</Label><Select value={formData.investor_type} onValueChange={(v) => handleChange('investor_type', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="institutional">Institutional</SelectItem><SelectItem value="individual">Individual</SelectItem><SelectItem value="family_office">Family Office</SelectItem></SelectContent></Select></div>
          <div><Label>Website</Label><Input value={formData.website_url} onChange={(e) => handleChange('website_url', e.target.value)} type="url" /></div>
        </div>
        <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => handleChange('description', e.target.value)} /></div>
        <div className="flex justify-end gap-4"><Button type="button" variant="outline" onClick={() => navigate('/admin/investors')}>Cancel</Button><Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEditing ? 'Update' : 'Create'}</Button></div>
      </form>
    </div>
  );
};

// Partner Form
export const PartnerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({ name: '', logo_alt: '', caption: '', website_url: '', partner_type: 'general', display_order: 0 });

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      axios.get(`${API}/admin/partners/${id}`, { withCredentials: true })
        .then(res => { setFormData(res.data); if (res.data.logo_url) setLogoPreview(res.data.logo_url.startsWith('/') ? `${BACKEND_URL}${res.data.logo_url}` : res.data.logo_url); })
        .catch(() => toast.error('Failed'))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  const handleLogoChange = (e) => { const file = e.target.files[0]; if (file) { setLogoFile(file); setLogoPreview(URL.createObjectURL(file)); } };
  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const res = isEditing
        ? await axios.put(`${API}/admin/partners/${id}`, formData, { withCredentials: true })
        : await axios.post(`${API}/admin/partners`, formData, { withCredentials: true });
      if (logoFile) {
        const fd = new FormData(); fd.append('file', logoFile); fd.append('alt_text', formData.logo_alt || formData.name);
        await axios.post(`${API}/admin/partners/${res.data.partner_id}/logo`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(isEditing ? 'Updated' : 'Created');
      navigate('/admin/partners');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" /></div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6"><Button variant="ghost" onClick={() => navigate('/admin/partners')}><ArrowLeft className="w-4 h-4" /></Button><h1 className="text-2xl font-bold">{isEditing ? 'Edit Partner' : 'Add Partner'}</h1></div>
      <form onSubmit={handleSubmit} className="max-w-2xl bg-white rounded-lg border p-6 space-y-6">
        <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required /></div>
        <div><Label>Logo</Label><div className="flex items-start gap-4">{logoPreview && <img src={logoPreview} alt="Preview" className="w-20 h-20 rounded-lg object-contain bg-gray-50 border" />}<div className="flex-1"><Input type="file" accept="image/*" onChange={handleLogoChange} className="mb-2" /><Input placeholder="Alt text" value={formData.logo_alt} onChange={(e) => handleChange('logo_alt', e.target.value)} /></div></div></div>
        <div className="grid md:grid-cols-2 gap-6">
          <div><Label>Type</Label><Select value={formData.partner_type} onValueChange={(v) => handleChange('partner_type', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="accelerator">Accelerator</SelectItem><SelectItem value="corporate">Corporate</SelectItem><SelectItem value="government">Government</SelectItem></SelectContent></Select></div>
          <div><Label>Website</Label><Input value={formData.website_url} onChange={(e) => handleChange('website_url', e.target.value)} type="url" /></div>
        </div>
        <div><Label>Caption</Label><Textarea value={formData.caption} onChange={(e) => handleChange('caption', e.target.value)} /></div>
        <div className="flex justify-end gap-4"><Button type="button" variant="outline" onClick={() => navigate('/admin/partners')}>Cancel</Button><Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEditing ? 'Update' : 'Create'}</Button></div>
      </form>
    </div>
  );
};

// News Form
export const NewsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: '', snippet: '', source_name: '', source_url: '', thumbnail_url: '', thumbnail_alt: '', published_date: new Date().toISOString().split('T')[0], tags: [], is_featured: false });
  const snippetEditorRef = React.useRef(null);

  const uploadNewsImageFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(`${API}/admin/news/upload-image`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
    const url = data.url;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const uploadNewsImageFromUrl = async (imageUrl) => {
    const { data } = await axios.post(`${API}/admin/news/upload-image-from-url`, { url: imageUrl }, { withCredentials: true });
    const url = data.url;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const snippetEditor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      ImageWithWrap.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#C8473A] underline' } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: 'Write news snippet (supports bold, italic, lists, links, images, tables)...' }),
    ],
    content: '',
    editorProps: {
      handlePaste: createImagePasteHandler(uploadNewsImageFile, () => snippetEditorRef.current),
    },
    onUpdate: ({ editor }) => setFormData(prev => ({ ...prev, snippet: editor.getHTML() })),
  });

  React.useEffect(() => {
    snippetEditorRef.current = snippetEditor;
  }, [snippetEditor]);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      axios.get(`${API}/admin/news/${id}`, { withCredentials: true })
        .then(res => {
          const data = res.data;
          if (data.published_date) data.published_date = data.published_date.split('T')[0];
          setFormData(data);
          if (snippetEditor && (data.snippet || '')) {
            const html = data.snippet.trim().startsWith('<') ? data.snippet : `<p>${(data.snippet || '').replace(/\n/g, '</p><p>')}</p>`;
            snippetEditor.commands.setContent(html);
          }
        })
        .catch(() => toast.error('Failed'))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing, snippetEditor]);

  useEffect(() => {
    if (snippetEditor && formData.snippet && snippetEditor.isEmpty) {
      const html = formData.snippet.trim().startsWith('<') ? formData.snippet : `<p>${(formData.snippet || '').replace(/\n/g, '</p><p>')}</p>`;
      snippetEditor.commands.setContent(html);
    }
  }, [snippetEditor, formData.snippet]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const snippetHtml = snippetEditor ? snippetEditor.getHTML() : formData.snippet;
    if (!formData.title || !formData.source_url) { toast.error('Fill required fields'); return; }
    if (!snippetHtml || snippetHtml === '<p></p>') { toast.error('Snippet is required'); return; }
    setSaving(true);
    try {
      const payload = { ...formData, snippet: snippetHtml, published_date: new Date(formData.published_date).toISOString() };
      isEditing
        ? await axios.put(`${API}/admin/news/${id}`, payload, { withCredentials: true })
        : await axios.post(`${API}/admin/news`, payload, { withCredentials: true });
      toast.success(isEditing ? 'Updated' : 'Created');
      navigate('/admin/news');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" /></div>;

  return (
    <div>
      <style>{snippetEditorStyles}</style>
      <div className="flex items-center gap-4 mb-6"><Button variant="ghost" onClick={() => navigate('/admin/news')}><ArrowLeft className="w-4 h-4" /></Button><h1 className="text-2xl font-bold">{isEditing ? 'Edit News' : 'Add News'}</h1></div>
      <form onSubmit={handleSubmit} className="max-w-2xl bg-white rounded-lg border p-6 space-y-6">
        <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => handleChange('title', e.target.value)} required /></div>
        <div>
          <Label>Snippet *</Label>
          <div className="news-snippet-editor mt-1">
            <NewsSnippetMenuBar editor={snippetEditor} onUploadImageFromUrl={uploadNewsImageFromUrl} />
            <EditorContent editor={snippetEditor} />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div><Label>Source Name *</Label><Input value={formData.source_name} onChange={(e) => handleChange('source_name', e.target.value)} placeholder="e.g., TechCrunch" required /></div>
          <div><Label>Published Date</Label><Input type="date" value={formData.published_date} onChange={(e) => handleChange('published_date', e.target.value)} /></div>
        </div>
        <div><Label>Source URL *</Label><Input value={formData.source_url} onChange={(e) => handleChange('source_url', e.target.value)} type="url" required /></div>
        <div><Label>Thumbnail URL</Label><Input value={formData.thumbnail_url} onChange={(e) => handleChange('thumbnail_url', e.target.value)} type="url" /></div>
        <div className="flex items-center gap-3"><Switch checked={formData.is_featured} onCheckedChange={(v) => handleChange('is_featured', v)} /><Label>Featured</Label></div>
        <div className="flex justify-end gap-4"><Button type="button" variant="outline" onClick={() => navigate('/admin/news')}>Cancel</Button><Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEditing ? 'Update' : 'Create'}</Button></div>
      </form>
    </div>
  );
};

// Regulatory Update Form
export const RegulatoryUpdateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    link_url: '',
    published_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      axios.get(`${API}/admin/regulatory-updates/${id}`, { withCredentials: true })
        .then(res => {
          const data = res.data;
          if (data.published_date) data.published_date = data.published_date.split('T')[0];
          setFormData((prev) => ({
            ...prev,
            title: data.title || '',
            summary: data.summary || '',
            link_url: data.link_url || '',
            published_date: data.published_date || prev.published_date,
          }));
        })
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.summary) { toast.error('Title and summary required'); return; }
    setSaving(true);
    try {
      const payload = { ...formData, published_date: new Date(formData.published_date).toISOString(), link_url: formData.link_url || null };
      isEditing
        ? await axios.put(`${API}/admin/regulatory-updates/${id}`, payload, { withCredentials: true })
        : await axios.post(`${API}/admin/regulatory-updates`, payload, { withCredentials: true });
      toast.success(isEditing ? 'Updated' : 'Created');
      navigate('/admin/regulatory-updates');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" /></div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6"><Button variant="ghost" onClick={() => navigate('/admin/regulatory-updates')}><ArrowLeft className="w-4 h-4" /></Button><h1 className="text-2xl font-bold">{isEditing ? 'Edit Regulatory Update' : 'Add Regulatory Update'}</h1></div>
      <form onSubmit={handleSubmit} className="max-w-2xl bg-white rounded-lg border p-6 space-y-6">
        <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => handleChange('title', e.target.value)} required /></div>
        <div><Label>Summary *</Label><Textarea value={formData.summary} onChange={(e) => handleChange('summary', e.target.value)} required rows={4} /></div>
        <div><Label>Link URL (optional)</Label><Input value={formData.link_url} onChange={(e) => handleChange('link_url', e.target.value)} type="url" placeholder="https://..." /></div>
        <div><Label>Published Date</Label><Input type="date" value={formData.published_date} onChange={(e) => handleChange('published_date', e.target.value)} /></div>
        <div className="flex justify-end gap-4"><Button type="button" variant="outline" onClick={() => navigate('/admin/regulatory-updates')}>Cancel</Button><Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEditing ? 'Update' : 'Create'}</Button></div>
      </form>
    </div>
  );
};
